import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogOut, Calculator, FileText, DollarSign, Menu as MenuIcon, X, List, Users, Wallet, Circle as XCircle, Calendar, Search, BarChart, Coins, ThumbsUp, TriangleAlert as AlertTriangle, SquareCheck as CheckSquare, Building2, ArrowRightLeft, CreditCard as Edit3, Bell, MapPin, TrendingDown, ClipboardCheck, TrendingUp, ChevronLeft, ChevronRight, CircleUser as UserCircle, CreditCard as Edit2, ClipboardList, Target, UserPlus, MessageSquare, ShieldBan, Activity, Bot, Banknote, Smartphone } from 'lucide-react';
import { supabase } from '../lib/supabase';
import UserAvatar from '../components/UserAvatar';

interface MenuItem {
  icon: React.ElementType;
  label: string;
  path: string;
}

interface MenuGroup {
  groupLabel: string;
  items: MenuItem[];
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [unreadNotifications, setUnreadNotifications] = React.useState(0);
  const [vendorReviewCount, setVendorReviewCount] = React.useState(0);
  const [pendingEditRequests, setPendingEditRequests] = React.useState(0);
  const [newLeadsCount, setNewLeadsCount] = React.useState(0);

  const menuGroups: MenuGroup[] = (() => {
    if (user?.role === 'seller') {
      return [
        {
          groupLabel: 'Vendas',
          items: [
            { icon: Calculator, label: 'Simulação de Empréstimo', path: '/dashboard/loan-simulation' },
            { icon: ClipboardCheck, label: 'Revisao de Cadastros', path: '/dashboard/vendor-review' },
          ],
        },
        {
          groupLabel: 'Contratos',
          items: [
            { icon: FileText, label: 'Contratos Pendentes', path: '/dashboard/contracts' },
            { icon: List, label: 'Meus Contratos', path: '/dashboard/my-contracts' },
            { icon: XCircle, label: 'Contratos Recusados', path: '/dashboard/rejected-contracts' },
          ],
        },
        {
          groupLabel: 'Cobrança',
          items: [
            { icon: Wallet, label: 'Cobrança', path: '/dashboard/collections-v2' },
            { icon: Calendar, label: 'Agenda', path: '/dashboard/manager-agenda' },
          ],
        },
        {
          groupLabel: 'Clientes',
          items: [
            { icon: Search, label: 'Clientes', path: '/dashboard/all-clients' },
            { icon: Edit2, label: 'Editar Clientes', path: '/dashboard/seller-client-edit' },
          ],
        },
        {
          groupLabel: 'Análise',
          items: [
            { icon: AlertTriangle, label: 'Inadimplentes', path: '/dashboard/overdue' },
            { icon: TrendingDown, label: 'Curvas', path: '/dashboard/curvas' },
          ],
        },
        {
          groupLabel: 'Leads',
          items: [
            { icon: UserPlus, label: 'Meus Leads', path: '/dashboard/my-leads' },
          ],
        },
        {
          groupLabel: 'WhatsApp',
          items: [
            { icon: MessageSquare, label: 'Meu WhatsApp', path: '/dashboard/meu-whatsapp' },
          ],
        },
        {
          groupLabel: 'Conta',
          items: [
            { icon: Bell, label: 'Notificações', path: '/dashboard/notifications' },
            { icon: UserCircle, label: 'Meu Perfil', path: '/dashboard/my-profile' },
          ],
        },
      ];
    }
    if (user?.role === 'manager') {
      return [
        {
          groupLabel: 'Operação',
          items: [
            { icon: FileText, label: 'Contratos', path: '/dashboard/contracts' },
            { icon: Calendar, label: 'Agenda', path: '/dashboard/manager-agenda' },
            { icon: Coins, label: 'Caixa', path: '/dashboard/caixa-v3' },
          ],
        },
        {
          groupLabel: 'Financeiro',
          items: [
            { icon: DollarSign, label: 'Financeiro', path: '/dashboard/financial' },
            { icon: BarChart, label: 'Dashboard Financeiro', path: '/dashboard/financial-dashboard' },
            { icon: Target, label: 'Metas', path: '/dashboard/metas' },
          ],
        },
        {
          groupLabel: 'Cobrança',
          items: [
            { icon: AlertTriangle, label: 'Inadimplentes', path: '/dashboard/overdue' },
            { icon: TrendingDown, label: 'Curvas', path: '/dashboard/curvas' },
          ],
        },
        {
          groupLabel: 'Clientes',
          items: [
            { icon: Search, label: 'Clientes', path: '/dashboard/all-clients' },
            { icon: Edit3, label: 'Editar Cliente', path: '/dashboard/client-edit' },
          ],
        },
        {
          groupLabel: 'Vendas',
          items: [
            { icon: BarChart, label: 'Vendas Diárias', path: '/dashboard/daily-sales' },
          ],
        },
        {
          groupLabel: 'Controle',
          items: [
            { icon: Banknote, label: 'Controle de Pagamentos', path: '/dashboard/pix-control' },
            { icon: ClipboardList, label: 'Revisao de Edicoes', path: '/dashboard/client-edit-review' },
            { icon: ThumbsUp, label: 'Avaliações', path: '/dashboard/contract-evaluations' },
            { icon: ShieldBan, label: 'Blacklist', path: '/dashboard/blacklist' },
            { icon: Bell, label: 'Notificações', path: '/dashboard/notifications' },
          ],
        },
        {
          groupLabel: 'Conta',
          items: [
            { icon: UserCircle, label: 'Meu Perfil', path: '/dashboard/my-profile' },
          ],
        },
        {
          groupLabel: 'Equipe',
          items: [
            { icon: Users, label: 'Vendedores e Motoboys', path: '/dashboard/sellers' },
            { icon: Activity, label: 'Atividade da Equipe', path: '/dashboard/team-activity' },
          ],
        },
        {
          groupLabel: 'Leads',
          items: [
            { icon: Target, label: 'Painel de Leads', path: '/dashboard/leads-manager' },
          ],
        },
        {
          groupLabel: 'Logística',
          items: [
            { icon: MapPin, label: 'Circuit (Rotas)', path: '/dashboard/circuit-settings' },
          ],
        },
        {
          groupLabel: 'WhatsApp',
          items: [
            { icon: MessageSquare, label: 'Configuração', path: '/dashboard/whatsapp-settings' },
            { icon: MessageSquare, label: 'Meu WhatsApp', path: '/dashboard/meu-whatsapp' },
            { icon: ClipboardList, label: 'Logs de Envio', path: '/dashboard/whatsapp-logs' },
          ],
        },
        ...(user?.unit_id === '42c1cf1f-dab1-4233-bc61-d2d94b5cb0ca' ? [{
          groupLabel: 'Agente de IA',
          items: [
            { icon: Bot, label: 'Painel do Agente', path: '/dashboard/ai-agent' },
          ],
        }] : []),
      ];
    }
    if (user?.role === 'motoboy') {
      return [
        {
          groupLabel: '',
          items: [
            { icon: Search, label: 'Clientes', path: '/dashboard/all-clients' },
            { icon: AlertTriangle, label: 'Inadimplentes', path: '/dashboard/overdue' },
            { icon: TrendingDown, label: 'Curvas', path: '/dashboard/curvas' },
          ],
        },
      ];
    }
    if (user?.role === 'admin') {
      return [
        {
          groupLabel: '',
          items: [
            { icon: Building2, label: 'Gerenciar Unidades', path: '/dashboard/units' },
            { icon: Users, label: 'Usuarios', path: '/dashboard/sellers' },
            { icon: Activity, label: 'Atividade da Equipe', path: '/dashboard/team-activity' },
            { icon: ArrowRightLeft, label: 'Transferir Clientes', path: '/dashboard/client-transfer' },
            { icon: Smartphone, label: 'App Cifrão', path: '/dashboard/app-cifrao' },
          ],
        },
        {
          groupLabel: 'WhatsApp',
          items: [
            { icon: MessageSquare, label: 'Configuração', path: '/dashboard/whatsapp-settings' },
            { icon: ClipboardList, label: 'Logs de Envio', path: '/dashboard/whatsapp-logs' },
          ],
        },
        ...(user?.unit_id === '42c1cf1f-dab1-4233-bc61-d2d94b5cb0ca' ? [{
          groupLabel: 'Agente de IA',
          items: [
            { icon: Bot, label: 'Painel do Agente', path: '/dashboard/ai-agent' },
          ],
        }] : []),
      ];
    }
    return [];
  })();

  const menuItems = menuGroups.flatMap(g => g.items);

  React.useEffect(() => {
    console.log('🔍 ADMIN DEBUG - Dashboard useEffect triggered');
    console.log('🔍 ADMIN DEBUG - Current user:', user);
    console.log('🔍 ADMIN DEBUG - User role:', user?.role);
    console.log('🔍 ADMIN DEBUG - Current location:', location.pathname);

    if (location.pathname === '/dashboard') {
      if (user?.role === 'seller') {
        console.log('🔍 ADMIN DEBUG - Redirecting to seller dashboard');
        navigate('/dashboard/loan-simulation');
      } else if (user?.role === 'manager') {
        console.log('🔍 ADMIN DEBUG - Redirecting to manager dashboard');
        navigate('/dashboard/contracts');
      } else if (user?.role === 'admin') {
        console.log('🔍 ADMIN DEBUG - Redirecting to admin dashboard');
        navigate('/dashboard/units');
      } else if (user?.role === 'motoboy') {
        console.log('🔍 ADMIN DEBUG - Redirecting to motoboy dashboard');
        navigate('/dashboard/all-clients');
      }
    }
  }, [user, location, navigate]);

  React.useEffect(() => {
    if (user) {
      fetchUnreadNotifications();
      setupRealtimeSubscription();
      if (user.role === 'seller') fetchVendorReviewCount();
      if (user.role === 'seller') fetchNewLeadsCount();
      if (user.role === 'manager') fetchPendingEditRequests();
      if (user.role === 'manager') fetchNewLeadsCount();
    }
  }, [user]);

  const fetchPendingEditRequests = async () => {
    if (!user) return;
    try {
      const { count } = await supabase
        .from('client_edit_requests')
        .select('*', { count: 'exact', head: true })
        .eq('unit_id', user.unit_id)
        .eq('status', 'pending');
      setPendingEditRequests(count || 0);
    } catch {
      // non-critical
    }
  };

  const fetchNewLeadsCount = async () => {
    if (!user) return;
    try {
      let query = supabase
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'distributed');

      if (user.role === 'seller') {
        query = query.eq('seller_id', user.id);
      } else if (user.role === 'manager') {
        query = query.eq('unit_id', user.unit_id);
      }

      const { count } = await query;
      setNewLeadsCount(count || 0);
    } catch {
      // non-critical
    }
  };

  const fetchVendorReviewCount = async () => {
    if (!user) return;
    try {
      const { count } = await supabase
        .from('contracts')
        .select('*', { count: 'exact', head: true })
        .eq('approval_status', 'vendor_review')
        .eq('seller_id', user.id);
      setVendorReviewCount(count || 0);
    } catch {
      // non-critical
    }
  };

  const fetchUnreadNotifications = async () => {
    if (!user) return;
    try {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('is_read', false);
      if (error) throw error;
      setUnreadNotifications(count || 0);
    } catch (error) {
      console.error('Error fetching unread notifications:', error);
    }
  };

  const setupRealtimeSubscription = () => {
    if (!user) return;
    const channel = supabase
      .channel('notifications-count')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        () => { fetchUnreadNotifications(); }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'client_edit_requests', filter: `unit_id=eq.${user.unit_id}` },
        () => { if (user.role === 'manager') fetchPendingEditRequests(); }
      )
      .subscribe();
    return () => { channel.unsubscribe(); };
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const roleLabel: Record<string, string> = {
    seller: 'Vendedor',
    manager: 'Gestor',
    motoboy: 'Motoboy',
    admin: 'Administrador',
  };

  const initials = user?.name
    ? user.name.slice(0, 2).toUpperCase()
    : '??';

  const getBadge = (path: string) => {
    if (path === '/dashboard/notifications' && unreadNotifications > 0)
      return { count: unreadNotifications > 99 ? '99+' : String(unreadNotifications), color: 'bg-red-500' };
    if (path === '/dashboard/vendor-review' && vendorReviewCount > 0)
      return { count: vendorReviewCount > 99 ? '99+' : String(vendorReviewCount), color: 'bg-orange-500' };
    if (path === '/dashboard/client-edit-review' && pendingEditRequests > 0)
      return { count: pendingEditRequests > 99 ? '99+' : String(pendingEditRequests), color: 'bg-orange-500' };
    if ((path === '/dashboard/my-leads' || path === '/dashboard/leads-manager') && newLeadsCount > 0)
      return { count: newLeadsCount > 99 ? '99+' : String(newLeadsCount), color: 'bg-green-500' };
    return null;
  };

  const SidebarContent = ({ mobile = false }: { mobile?: boolean }) => (
    <div className={`flex flex-col h-full ${mobile ? 'w-72' : isCollapsed ? 'w-[70px]' : 'w-60'} transition-all duration-300`}>

      {/* Header */}
      <div className={`flex-shrink-0 border-b border-gray-100 ${isCollapsed && !mobile ? 'px-2 pt-5 pb-4' : 'px-4 pt-6 pb-4'}`}>

        {/* Branding */}
        {(!isCollapsed || mobile) ? (
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full flex flex-col items-center gap-2 mb-5 group focus:outline-none"
          >
            {/* Logo image with badge background */}
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center shadow-sm ring-1 ring-blue-100 group-hover:ring-blue-200 group-hover:shadow-md transition-all duration-200">
                <img
                  src="/ChatGPT_Image_18_de_jun._de_2026,_22_34_48.png"
                  alt="Ruta Controller"
                  className="h-10 w-10 object-contain"
                />
              </div>
              {/* Status dot */}
              <span className="absolute bottom-0.5 right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full ring-2 ring-white" />
            </div>

            {/* Name + subtitle */}
            <div className="text-center">
              <p className="text-sm font-bold text-gray-800 tracking-tight leading-tight">{user?.unit_name || 'Cifrao'}</p>
            </div>
          </button>
        ) : (
          /* Collapsed — icon only, centered */
          <button
            onClick={() => navigate('/dashboard')}
            className="flex justify-center mb-4 group focus:outline-none"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center shadow-sm ring-1 ring-blue-100 group-hover:ring-blue-200 group-hover:shadow-md transition-all duration-200">
              <img
                src="/ChatGPT_Image_18_de_jun._de_2026,_22_34_48.png"
                alt="Ruta Controller"
                className="h-7 w-7 object-contain"
              />
            </div>
          </button>
        )}

        {/* User info */}
        {(!isCollapsed || mobile) ? (
          <div className="flex items-center gap-2.5 bg-gray-50 rounded-xl p-2.5">
            <UserAvatar
              name={user?.displayName || user?.name || ''}
              role={user?.role ?? 'seller'}
              size="sm"
              showTooltip={false}
            />
            <div className="overflow-hidden flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-800 truncate">{user?.displayName || user?.name || '—'}</p>
              <p className="text-[10px] text-gray-400 truncate">{roleLabel[user?.role || ''] || ''}</p>
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <UserAvatar
              name={user?.displayName || user?.name || ''}
              role={user?.role ?? 'seller'}
              size="sm"
              showTooltip
            />
          </div>
        )}
      </div>

      {/* Nav items */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 scrollbar-thin">
        {menuGroups.map((group, gi) => (
          <div key={gi} className={gi > 0 ? 'mt-3' : ''}>
            {/* Group label — hidden when collapsed (desktop) */}
            {group.groupLabel && (!isCollapsed || mobile) && (
              <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400 select-none">
                {group.groupLabel}
              </p>
            )}
            {/* Collapsed divider between groups */}
            {group.groupLabel && isCollapsed && !mobile && gi > 0 && (
              <div className="mx-2 mb-1 border-t border-gray-100" />
            )}

            <div className="space-y-0.5">
              {group.items.map((item) => {
                const isActive = location.pathname === item.path;
                const badge = getBadge(item.path);

                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setIsMobileMenuOpen(false);
                    }}
                    aria-label={item.label}
                    aria-current={isActive ? 'page' : undefined}
                    title={isCollapsed && !mobile ? item.label : undefined}
                    className={`
                      group relative w-full flex items-center gap-3 rounded-lg
                      transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                      ${isCollapsed && !mobile ? 'justify-center px-2 py-2.5' : 'px-3 py-2.5'}
                      ${isActive
                        ? 'bg-blue-600 text-white shadow-sm shadow-blue-200'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }
                    `}
                  >
                    {isActive && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-white/60 rounded-full" />
                    )}

                    <item.icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-700'}`} />

                    {(!isCollapsed || mobile) && (
                      <>
                        <span className="text-sm font-medium truncate flex-1 text-left">{item.label}</span>
                        {badge && (
                          <span className={`${badge.color} text-white text-[10px] font-bold rounded-full min-w-[18px] h-4 flex items-center justify-center px-1 flex-shrink-0`}>
                            {badge.count}
                          </span>
                        )}
                      </>
                    )}

                    {isCollapsed && !mobile && badge && (
                      <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
                    )}

                    {isCollapsed && !mobile && (
                      <div className="absolute left-full ml-2.5 px-2.5 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-lg whitespace-nowrap
                        opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50 shadow-lg">
                        {item.label}
                        {badge && <span className="ml-1.5 text-red-300">({badge.count})</span>}
                        <span className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="flex-shrink-0 px-2 py-3 border-t border-gray-100">
        <button
          onClick={handleLogout}
          aria-label="Sair do sistema"
          title={isCollapsed && !mobile ? 'Sair' : undefined}
          className={`
            group w-full flex items-center gap-3 rounded-lg px-3 py-2.5
            text-gray-500 hover:bg-red-50 hover:text-red-600
            transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400
            ${isCollapsed && !mobile ? 'justify-center px-2' : ''}
          `}
        >
          <LogOut className="w-4 h-4 flex-shrink-0 group-hover:text-red-500" />
          {(!isCollapsed || mobile) && <span className="text-sm font-medium">Sair</span>}

          {/* Collapsed logout tooltip */}
          {isCollapsed && !mobile && (
            <div className="absolute left-full ml-2.5 px-2.5 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-lg whitespace-nowrap
              opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50 shadow-lg">
              Sair
              <span className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
            </div>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-gray-50">

      {/* ── Desktop sidebar ── */}
      <aside className={`hidden lg:flex flex-col flex-shrink-0 bg-white border-r border-gray-100 shadow-sm relative
        ${isCollapsed ? 'w-[70px]' : 'w-60'} transition-all duration-300`}>
        <SidebarContent />

        {/* Collapse toggle */}
        <button
          onClick={() => setIsCollapsed(v => !v)}
          aria-label={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
          className="absolute -right-3 top-20 w-6 h-6 bg-white border border-gray-200 rounded-full shadow-sm
            flex items-center justify-center text-gray-400 hover:text-gray-700 hover:border-gray-300
            transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 z-10"
        >
          {isCollapsed
            ? <ChevronRight className="w-3.5 h-3.5" />
            : <ChevronLeft className="w-3.5 h-3.5" />
          }
        </button>
      </aside>

      {/* ── Mobile top bar ── */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-gray-100 shadow-sm z-50 flex items-center justify-between px-4">
        <button
          onClick={() => setIsMobileMenuOpen(true)}
          aria-label="Abrir menu"
          className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <MenuIcon className="w-5 h-5" />
        </button>

        <img
          src="/ChatGPT_Image_18_de_jun._de_2026,_22_34_48.png"
          alt="Ruta Controller"
          className="h-8 w-auto object-contain"
        />

        {/* Notification dot on mobile topbar */}
        <div className="w-9 h-9 flex items-center justify-center relative">
          {unreadNotifications > 0 && (
            <button
              onClick={() => { navigate('/dashboard/notifications'); }}
              className="relative w-9 h-9 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label={`${unreadNotifications} notificações não lidas`}
            >
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
            </button>
          )}
        </div>
      </div>

      {/* ── Mobile drawer overlay ── */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ── Mobile drawer ── */}
      <div className={`
        lg:hidden fixed top-0 left-0 h-full z-50 bg-white shadow-2xl pb-[env(safe-area-inset-bottom)]
        transition-transform duration-300 ease-in-out overflow-hidden
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex items-center justify-end px-4 h-14 border-b border-gray-100 flex-shrink-0">
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="Fechar menu"
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="h-[calc(100%-56px)] overflow-y-auto">
          <SidebarContent mobile />
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="flex-1 min-w-0 flex flex-col">
        <main className="flex-1 p-4 lg:p-6 pt-[72px] lg:pt-6 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
